import { useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatMoney, type CashForecastView } from '../api';
import { formatDay, formatLongDay, useDeskData } from '../components/desk';
import { BrokenThisWeek, CashWindows } from '../components/CashExpected';
import './cash.css';

type Mode = 'month' | 'week';
type Range = { from: string; to: string };
type Day = NonNullable<CashForecastView['calendar']>[number];
type Item = Day['items'][number];
const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const stateLabels: Record<string, string> = {
  proposed: 'Proposed', promised: 'Promised', overdue: 'Overdue', kept: 'Kept', broken: 'Broken',
};

const parse = (date: string) => new Date(`${date}T00:00:00Z`);
const iso = (date: Date) => date.toISOString().slice(0, 10);
const addDays = (date: string, days: number) => {
  const value = parse(date); value.setUTCDate(value.getUTCDate() + days); return iso(value);
};
const mondayOf = (date: string) => addDays(date, -((parse(date).getUTCDay() + 6) % 7));
const weekOf = (date: string): Range => { const from = mondayOf(date); return { from, to: addDays(from, 6) }; };
const monthOf = (date: string): Range => {
  const value = parse(date);
  const from = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), 1));
  const to = new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + 1, 0));
  return { from: iso(from), to: iso(to) };
};
const shiftMonth = (date: string, months: number): Range => {
  const value = parse(date);
  return monthOf(iso(new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth() + months, 1))));
};
const monthLabel = (date: string) => new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
  .format(parse(date));
const money = (values: { minor: string; currency: string }[]) => values.map(value => formatMoney(value.minor, value.currency)).join(' · ');

function StateBadge({ item }: { item: Item }) {
  return <span className={`cash-state ${item.state}`}>{stateLabels[item.state] ?? item.state}</span>;
}

function DayDetail({ day, asOf }: { day: Day | undefined; asOf: string }) {
  if (!day) return null;
  return <section className="cash-day-detail" aria-labelledby="cash-day-title">
    <header><div><h2 id="cash-day-title">{formatLongDay(day.date)}</h2>
      <p>{day.items.length ? `${day.promisedCount ? `${day.promisedCount} promised` : ''}${day.promisedCount && day.collectedCount ? ' and ' : ''}${day.collectedCount ? `${day.collectedCount} collected` : ''}.`
        : 'No promises fall due and no payments land on this day.'}</p></div>
      {day.promised.length > 0 && <div className="cash-day-total"><span>Promised</span><strong>{money(day.promised)}</strong></div>}
      {day.collected.length > 0 && <div className="cash-day-total collected"><span>Collected</span><strong>{money(day.collected)}</strong></div>}
    </header>
    {day.items.length > 0 && <ul className="cash-detail-list">
      {day.items.map(item => <li key={item.promiseId}>
        <div className="cash-detail-who">
          <a href={`#customers/${encodeURIComponent(item.customerId)}`}>{item.customerName}</a>
          <small>{item.invoiceNumbers.join(', ') || 'No invoice linked'} · promised {formatDay(item.promisedOn, asOf)}</small>
        </div>
        <div className="cash-detail-money">
          {item.expectedMinor !== '0' && <span><small>Promised</small>{formatMoney(item.expectedMinor, item.currency)}</span>}
          {item.collectedMinor !== '0' && <span className="collected"><small>Collected</small>{formatMoney(item.collectedMinor, item.currency)}</span>}
          <StateBadge item={item} />
        </div>
      </li>)}
    </ul>}
  </section>;
}

/** The month and week promise calendar, with a day drill-down that links every customer to their desk. */
export function Cash() {
  const [mode, setMode] = useState<Mode>('month');
  const [requested, setRequested] = useState<Range | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const path = requested ? `/app-api/cash/forecast?from=${requested.from}&to=${requested.to}` : '/app-api/cash/forecast';
  const { data: view, error } = useDeskData<CashForecastView>(path);
  const days = view?.calendar ?? [];
  const range: Range | null = requested ?? (view ? view.month : null);
  // Week view opens on the selected day, else today if it is in range, else the first day that holds a promise.
  const anchor = selected ?? (view?.asOf && range && view.asOf >= range.from && view.asOf <= range.to ? view.asOf
    : days.find(day => day.items.length)?.date ?? range?.from) ?? view?.asOf;
  const activeDate = selected && days.some(day => day.date === selected) ? selected
    : view && days.some(day => day.date === view.asOf) ? view.asOf : days.find(day => day.items.length)?.date ?? days[0]?.date;
  const activeDay = days.find(day => day.date === activeDate);
  const leading = range ? (parse(range.from).getUTCDay() + 6) % 7 : 0;
  const label = range ? mode === 'month' ? monthLabel(range.from)
    : `${formatDay(range.from, view?.asOf)} – ${formatDay(range.to, view?.asOf)}` : '';

  const show = (next: Mode) => {
    if (!anchor) return;
    setMode(next);
    setRequested(next === 'week' ? weekOf(anchor) : monthOf(anchor));
    setSelected(null);
  };
  const navigate = (direction: number) => {
    if (!range) return;
    setRequested(mode === 'week'
      ? { from: addDays(range.from, direction * 7), to: addDays(range.to, direction * 7) }
      : shiftMonth(range.from, direction));
    setSelected(null);
  };
  return <main id="main-content" className="queue-page cash-page">
    <header className="page-heading"><div><span className="desk-date">{view ? formatLongDay(view.asOf) : 'Loading'}</span>
      <h1>Cash expected<span className="heading-dot" aria-hidden="true">.</span></h1>
      <p>{view ? `Promised money by date, and a separate estimate from how customers actually pay.`
        : 'Reading promises and payment history…'}</p></div></header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {view && <>
      <CashWindows view={view} />
      <section className="cash-calendar" aria-labelledby="cash-calendar-title">
        <header className="cash-calendar-head">
          <div><h2 id="cash-calendar-title"><CalendarDays size={18} aria-hidden />Promise calendar</h2>
            <p className="cash-range" aria-live="polite">{label}</p></div>
          <div className="cash-calendar-controls">
            <div className="cash-view-toggle" role="group" aria-label="Calendar view">
              <button type="button" aria-pressed={mode === 'month'} onClick={() => show('month')}>Month</button>
              <button type="button" aria-pressed={mode === 'week'} onClick={() => show('week')}>Week</button>
            </div>
            <button type="button" className="cash-nav" aria-label={`Previous ${mode}`} onClick={() => navigate(-1)}>
              <ChevronLeft size={16} aria-hidden /></button>
            <button type="button" className="cash-nav" aria-label={`Next ${mode}`} onClick={() => navigate(1)}>
              <ChevronRight size={16} aria-hidden /></button>
          </div>
        </header>
        <div className={`cash-grid ${mode}`}>
          {weekdays.map(day => <span key={day} className="cash-weekday" aria-hidden>{day}</span>)}
          {Array.from({ length: leading }, (_, index) => <span key={`blank-${index}`} className="cash-day blank" aria-hidden />)}
          {days.map(day => {
            const description = [
              day.promisedCount ? `${money(day.promised)} promised across ${day.promisedCount} ${day.promisedCount === 1 ? 'promise' : 'promises'}` : 'Nothing promised',
              day.collected.length ? `${money(day.collected)} collected` : '',
              day.overdueCount ? `${day.overdueCount} overdue` : '', day.keptCount ? `${day.keptCount} kept` : '',
              day.brokenCount ? `${day.brokenCount} broken` : '',
            ].filter(Boolean).join(', ');
            return <button type="button" key={day.date} onClick={() => setSelected(day.date)}
              aria-label={`${formatLongDay(day.date)}. ${description}`} aria-pressed={day.date === activeDate}
              className={`cash-day ${day.date === activeDate ? 'selected' : ''} ${day.date === view.asOf ? 'today' : ''} ${day.overdueCount ? 'overdue' : ''}`}>
              <span className="cash-day-number">{Number(day.date.slice(8))}</span>
              {day.promised.length > 0 && <span className="cash-day-money">{day.promised.map(value =>
                <span key={value.currency}>{formatMoney(value.minor, value.currency)}</span>)}</span>}
              {day.promisedCount > 0 && <small>{day.promisedCount} {day.promisedCount === 1 ? 'promise' : 'promises'}</small>}
              {day.collected.length > 0 && <span className="cash-day-collected">{day.collected.map(value =>
                <span key={value.currency}>{formatMoney(value.minor, value.currency)} collected</span>)}</span>}
              <span className="cash-day-flags">
                {day.overdueCount > 0 && <em className="overdue">Overdue</em>}
                {day.keptCount > 0 && <em className="kept">Kept</em>}
                {day.brokenCount > 0 && <em className="broken">Broken</em>}
              </span>
            </button>;
          })}
        </div>
      </section>
      <DayDetail day={activeDay} asOf={view.asOf} />
      <BrokenThisWeek view={view} />
    </>}
  </main>;
}
